import { useMemo } from 'react';
import * as z from 'zod';

import type { ParcelFormValues } from './useParcelFormSchema';
import { parcelFormSchema } from './useParcelFormSchema';

/** Quantidade mínima de parcelas quando a recorrência está ligada. */
const MIN_RECURRENCE_AMOUNT = 2;

/**
 * Schema do formulário de criação de parcela.
 *
 * É o `parcelFormSchema` compartilhado — ver `useParcelFormSchema` para a
 * derivação a partir do schema gerado pelo Kubb e para o motivo de as duas telas
 * compartilharem um shape só.
 *
 * A criação **não renderiza** os campos que pertencem à edição (nota fiscal,
 * fração de parcelas, Nosso Número e as duas flags de reset), e
 * `buildParcelCreatePayload` também não os envia: o corpo do `POST` lista campo a
 * campo o que a tela controla.
 */
export const parcelCreateFormSchema = parcelFormSchema;

/** Valores do formulário de criação de parcela. */
export type ParcelCreateFormValues = ParcelFormValues;

/**
 * Schema de envio — o do formulário mais a regra da recorrência.
 *
 * A quantidade é endurecida por `superRefine`, e não por `.extend()`, para que o
 * tipo inferido continue sendo `ParcelCreateFormValues`: `amountOfParcel` só é
 * obrigatório quando o interruptor está ligado, e trocá-lo para obrigatório no
 * shape mudaria o tipo do formulário inteiro.
 *
 * O mínimo é 2 porque uma "recorrência de uma parcela" é a própria parcela — o
 * endpoint gera cópias a partir da original.
 */
export const parcelCreateSchema = parcelCreateFormSchema.superRefine((values, ctx) => {
  if (values.multiple !== true) return;

  // O schema gerado passa por `ToZod` e o Zod infere o campo de forma opaca; a
  // checagem de tipo aqui é o que devolve a comparação numérica.
  const amount = typeof values.amountOfParcel === 'number' ? values.amountOfParcel : null;

  if (amount === null || amount < MIN_RECURRENCE_AMOUNT) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['amountOfParcel'],
      message: 'parcelCreate.validation.amountRange',
    });
  }
});

/**
 * Schema e valores iniciais do formulário de criação de parcela.
 *
 * Os defaults são deliberadamente vazios: nada aqui pode ser sugerido sem risco.
 * O número da parcela não sai de `numberParcels` do contrato — aquele é o total
 * contratado, não o próximo número livre, e o backend recusa duplicidade por
 * tipo, que a tela não conhece.
 *
 * @returns `{ schema, defaultValues }` estáveis entre renders.
 *
 * @example
 * ```tsx
 * const { schema, defaultValues } = useParcelCreateFormSchema();
 * const form = useForm({ resolver: zodResolver(schema), defaultValues });
 * ```
 */
export function useParcelCreateFormSchema(): {
  readonly schema: typeof parcelCreateSchema;
  readonly defaultValues: Partial<ParcelCreateFormValues>;
} {
  const defaultValues = useMemo<Partial<ParcelCreateFormValues>>(
    () => ({
      expirationDate: '',
      message: '',
      observation: '',
      multiple: false,
    }),
    [],
  );

  return { schema: parcelCreateSchema, defaultValues } as const;
}
